(function () {
  "use strict";

  if (!document.documentElement.hasAttribute("data-of-input-mode-ready")) {
    document.documentElement.setAttribute("data-of-input-mode-ready", "true");
    document.addEventListener("keydown", function (event) {
      if (event.key === "Tab") document.documentElement.classList.add("of-keyboard-nav");
    }, true);
    document.addEventListener("pointerdown", function () {
      document.documentElement.classList.remove("of-keyboard-nav");
    }, true);
  }

  function makeArrow(direction) {
    var button = document.createElement("button");
    button.type = "button";
    button.className = "of-product-nav of-product-nav--" + direction;
    button.setAttribute("aria-label", direction === "prev" ? "Previous product image" : "Next product image");
    return button;
  }

  function announce(card, variantId, reason) {
    card.dispatchEvent(new CustomEvent("of-product-preview", {
      bubbles: true,
      detail: { card: card, variantId: variantId, reason: reason || "programmatic" }
    }));
  }

  function scheduleIdle(callback) {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(callback, { timeout: 2000 });
    } else {
      window.setTimeout(callback, 200);
    }
  }

  // Warms the browser's HTTP cache for one variant's images so setImage()'s src swap
  // resolves from cache instead of a fresh network request. `seen` is shared across calls
  // so the same URL is never requested twice.
  function prefetchVariantImages(entry, seen) {
    if (!entry) return;
    if (entry.featured && !seen[entry.featured]) {
      seen[entry.featured] = true;
      new Image().src = entry.featured;
    }
    (entry.gallery || []).forEach(function (item) {
      if (item && item.src && !seen[item.src]) {
        seen[item.src] = true;
        new Image().src = item.src;
      }
    });
  }

  // Warms every variant's images. Deferred to idle time since it competes with the page's
  // above-the-fold loads otherwise — fine for the model/color chips, which are a second
  // interaction away, but too slow for the active variant's own gallery arrows (see the
  // immediate prefetchVariantImages call in initCard, right after the first paint).
  function prefetchGalleryImages(variants, seen) {
    seen = seen || {};
    Object.keys(variants).forEach(function (id) {
      prefetchVariantImages(variants[id], seen);
    });
  }

  function initCard(card) {
    var payloadScript = card.querySelector("[data-of-product-card-payload]");
    var image = card.querySelector(".of-product-image");
    var media = card.querySelector(".of-product-media");
    var hit = card.querySelector(".of-card-hit");
    var price = card.querySelector(".of-product-price");
    var subtitle = card.querySelector(".of-product-subtitle");
    var meta = card.querySelector(".of-product-meta");
    var modelHost = card.querySelector(".of-models");
    var pdpLink = card.querySelector(".of-product-pdp-link");
    if (!payloadScript || !image || !media || !hit) return;

    var payload;
    try {
      payload = JSON.parse(payloadScript.textContent);
    } catch (error) {
      return;
    }

    var variants = payload.variants || {};
    var variantIds = Object.keys(variants);
    var previewOnly = payload.mode === "preview";
    // payload.active is a JSON number, but data-model-id (and every other id this gets
    // compared against) is a DOM attribute string — coerce once here so aria-pressed
    // actually matches on first render instead of only after the first swatch click
    // (post-click ids come from Object.keys(variants), which are already strings).
    var activeId = String(payload.active);
    var galleryIndex = -1;

    var prev = makeArrow("prev");
    var next = makeArrow("next");
    var status = document.createElement("span");
    status.className = "of-product-status";
    status.setAttribute("aria-live", "polite");
    media.appendChild(prev);
    media.appendChild(next);
    media.appendChild(status);

    card.classList.toggle("is-preview-only", previewOnly);
    card.classList.toggle("is-view-link-only", !previewOnly);

    function currentEntry() {
      return variants[activeId];
    }

    function displayGallery(entry) {
      var gallery = (entry.gallery || []).slice();
      if (entry.featured) {
        var featured = { src: entry.featured, alt: entry.label || "", isFeatured: true };
        var existingIndex = gallery.findIndex(function (item) { return item.src === entry.featured; });
        if (existingIndex === -1) return [featured].concat(gallery);
      }
      return gallery;
    }

    function announceImage(items) {
      var item = items[galleryIndex];
      if (!item) return;
      status.textContent = "Image " + (galleryIndex + 1) + " of " + items.length + ": " + item.alt;
    }

    function setImage(src, alt) {
      if (!src) return;
      image.classList.add("is-switching");
      // The server-rendered <img> carries a srcset (Shopify's responsive image_tag), which
      // browsers resolve in preference to src — leaving it in place after a JS-driven swap
      // means currentSrc stays on the old image even though src updates, so nothing visibly
      // changes. The demo's own <img> has no srcset (static export), so this never surfaced
      // there; clear it here so src actually takes effect.
      image.removeAttribute("srcset");
      image.src = src;
      image.alt = alt || "";
      window.requestAnimationFrame(function () {
        image.classList.remove("is-switching");
      });
    }

    function renderFeatured(entry) {
      var items = displayGallery(entry);
      if (!items.length) return;
      galleryIndex = 0;
      setImage(items[galleryIndex].src, items[galleryIndex].alt);
    }

    function updateModelButtons() {
      if (!modelHost) return;
      Array.from(modelHost.querySelectorAll("button")).forEach(function (button) {
        button.setAttribute("aria-pressed", button.getAttribute("data-model-id") === activeId ? "true" : "false");
      });
    }

    function updateHit(entry) {
      if (pdpLink) {
        pdpLink.href = entry.href;
      }
      if (previewOnly) {
        hit.setAttribute("aria-label", "Preview " + (entry.label || "product"));
        return;
      }
      hit.setAttribute("aria-hidden", "true");
    }

    function setVariant(id, shouldAnnounce, reason) {
      var entry = variants[id];
      if (!entry) return;
      activeId = id;
      card.setAttribute("data-active-variant-id", id);
      updateHit(entry);
      if (price) price.textContent = entry.price;
      if (subtitle && entry.subtitle) subtitle.textContent = entry.subtitle;
      renderFeatured(entry);
      updateModelButtons();
      if (shouldAnnounce) announce(card, id, reason);
    }

    function step(direction) {
      var entry = currentEntry();
      if (!entry) return;
      var gallery = displayGallery(entry);
      if (!gallery.length) return;
      galleryIndex = (galleryIndex + direction + gallery.length) % gallery.length;
      setImage(gallery[galleryIndex].src, gallery[galleryIndex].alt);
      announceImage(gallery);
    }

    prev.addEventListener("click", function (event) {
      event.preventDefault();
      event.stopPropagation();
      step(-1);
    });

    next.addEventListener("click", function (event) {
      event.preventDefault();
      event.stopPropagation();
      step(1);
    });

    if (previewOnly) {
      hit.addEventListener("click", function (event) {
        event.preventDefault();
        announce(card, activeId, "card-control");
      });
    }

    if (modelHost && variantIds.length > 1) {
      variantIds.forEach(function (id) {
        var entry = variants[id];
        var button = document.createElement("button");
        button.type = "button";
        button.className = "of-model-chip";
        button.setAttribute("data-model-id", id);
        button.setAttribute("aria-label", "Select " + (entry.label || "model"));
        button.setAttribute("data-analytics-event", "select_item");
        button.setAttribute("data-analytics-content-id", id);
        button.title = entry.label || "";
        if (entry.swatchColor) button.style.setProperty("--model-color", entry.swatchColor);
        button.addEventListener("click", function (event) {
          event.preventDefault();
          event.stopPropagation();
          setVariant(id, true, "model-control");
        });
        modelHost.appendChild(button);
      });
    }

    card.addEventListener("mouseenter", function () {
      if (previewOnly) announce(card, activeId, "hover");
    });

    card.addEventListener("focusin", function () {
      if (previewOnly) announce(card, activeId, "focus");
    });

    setVariant(activeId, false);

    // Active variant's own gallery (what the arrows step through) is the most likely next
    // interaction, so fetch it right away rather than waiting for an idle slot.
    var prefetchedSrcs = {};
    prefetchVariantImages(variants[activeId], prefetchedSrcs);

    scheduleIdle(function () {
      prefetchGalleryImages(variants, prefetchedSrcs);
    });
  }

  Array.from(document.querySelectorAll("[data-of-product-card]")).forEach(initCard);
  document.dispatchEvent(new CustomEvent("of-product-cards-ready"));
})();
